import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-button-group} KatalButtongroup provides an ability to the user to select one of the multiple button options.
 * @status production
 * @theme flo
 * @guideline Do Each button provided is a separate option.
 * @contentProvided <kat-button label="First button"></kat-button>
 * @event change CustomEvent triggered when a user chooses an option. The event's detail.value gives the selected value.
 * @example SentimentChoice {"value": "", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselected {"value": "neutral", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselectedDisabled {"value": "neutral", "disabled": true, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 */
class KatalButtongroup extends KatalComponent {
  static get observedAttributes() {
    return ['value', 'disabled'];
  }

  constructor() {
    super(KatalButtongroup.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.addButtonEventListeners();
    this.listenForChildren();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
    this._observer.disconnect();
  }

  listenForChildren() {
    this._observer = new MutationObserver(mutationsList => {
      // If the mutation detected was the addition of any tag that started with the name "kat-button" then request a re-render
      if (
        mutationsList.some(
          mutation =>
            mutation.type == 'childList' &&
            mutation.addedNodes &&
            Array.from(mutation.addedNodes).some(
              addedNode =>
                addedNode.tagName && addedNode.tagName.includes('KAT-BUTTON')
            )
        )
      ) {
        this.render();
      }
    });
    this._observer.observe(this, { childList: true });
  }

  clickHandler(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.disabled) {
      //leaving this here for saftey, however this should never be called because the kat-button's themselves are being marked as disabled.
      /* istanbul ignore next */
      return;
    }

    const selectedButton = event.target.closest('*[value]');

    //There doesnt seem to be a way to test for this exception in headless chrome
    /* istanbul ignore next */
    if (!selectedButton) {
      /* istanbul ignore next */
      throw new Error(
        'Expected kat-button-group buttons to have a value attribute'
      );
    }
    const selectedButtonValue = selectedButton.value;
    const currentValue = this.value;

    if (selectedButtonValue === currentValue) {
      return;
    } else {
      this.value = selectedButtonValue; // Will re-render the component
    }

    if (event.type !== 'change' && event.type !== 'input') {
      this.dispatchCustomEvent('change', { value: selectedButtonValue });
    }
  }

  addButtonEventListeners() {
    this.addEventListener('click', this.clickHandler);
  }

  /**
   * @param {string} value This attribute will be set on click of any button in the group to that button's value
   */
  get value() {
    return this.getAttribute('value') || '';
  }

  set value(value) {
    this.setAttribute('value', value);
  }

  /**
   * @param {boolean} disabled When kat-button-group is disabled, no buttons can be clicked
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }

  set disabled(value) {
    this.setBooleanAttribute('disabled', value);
  }

  render() {
    const selectedValue = this.value;
    const disabled = this.disabled;

    const childButtons = Array.from(this.childNodes).filter(e => {
      return e.tagName === 'KAT-BUTTON';
    });

    childButtons.forEach(buttonElement => {
      buttonElement.disabled = disabled;
      if (buttonElement.getAttribute('value') === selectedValue) {
        buttonElement.classList.add('selected');
      } else {
        buttonElement.classList.remove('selected');
      }
    });
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
    }
  }
}

KatalComponent.register('kat-button-group', KatalButtongroup);
